import { DEFAULT_PRO_FILTER_SET, Service, useDataService } from 'services/data.service';
import { AxiosProgressEvent } from 'axios';
import { addDays } from 'date-fns';
import { useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useQuery } from '@tanstack/react-query';
import { useDataRequest } from 'hooks/useDataRequest';

export type CostConfigStatus = 'UPDATED' | 'SCHEDULED' | 'NONE';
export type CostConfigKey = 'cogs' | 'marketplace_fees' | 'payment_fees' | 'logistics_fees';

export interface CostConfigCardData {
  key: CostConfigKey;
  status: CostConfigStatus;
  statusDate?: Date;
}

interface FeeUploadStatus {
  status: string;
  updatedOn?: Date;
}

export interface CostConfigUploadResult {
  success: boolean;
  fileName: string | null;
  errors: string[];
}

interface UploadError {
  sheet: string | null;
  row: number | null;
  column: string | null;
  error: string;
}

export type CostUploadStatus = 'FAILED' | 'UPLOADED';

export interface CostConfigurationUpload {
  user: string;
  uploadId: string;
  fileName: string;
  changedFiles: string;
  uploadDateTime: Date;
  uploadStatus: CostUploadStatus;
  errorCount: number;
  changeCount: number;
}

function describeUploadError({ sheet, column, row, error }: UploadError): string {
  const location = [
    sheet ? `Sheet: ${sheet}` : null,
    column ? `Column: ${column}` : null,
    row != null ? `Row: ${row}` : null,
  ]
    .filter(part => part !== null)
    .join(', ');
  return location ? `${location} - ${error}` : error;
}

const UPLOAD_CONFIG_ENDPOINT = 'upload';

const DOWNLOAD_ALL_CONFIGS_ENDPOINT = 'downloadCurrentConfigs';

export function useCostConfigurationCards(isModalOpen: boolean) {
  const { accountNumber } = useContext(TenantConfigContext);

  const requestData = useDataRequest();

  const getData = useDataService(Service.Profitability, DEFAULT_PRO_FILTER_SET).get<Record<string, FeeUploadStatus>>(
    'upload-statuses'
  );

  return useQuery({
    queryKey: ['upload-statuses', accountNumber, requestData, isModalOpen],
    queryFn: async (): Promise<CostConfigCardData[]> => {
      const response = await getData('', requestData);
      if (response.error) {
        throw new Error(response.error.message);
      }
      const resultRecord = response.data ?? {};

      return Object.getOwnPropertyNames(resultRecord).map(key => {
        const uploadStatus = resultRecord[key];
        const uploadDate = uploadStatus.updatedOn ? new Date(uploadStatus.updatedOn) : undefined;

        return {
          key: key as CostConfigKey,
          status: uploadStatus.status,
          statusDate: uploadDate
            ? uploadStatus.status === 'SCHEDULED'
              ? addDays(uploadDate, 1)
              : uploadDate
            : undefined,
        } as CostConfigCardData;
      });
    },
    staleTime: 0,
  });
}

function triggerFileDownload(data: Blob, filename: string) {
  const fileURL = window.URL.createObjectURL(data);
  const alink = document.createElement('a');
  alink.href = fileURL;
  alink.download = filename;
  alink.click();
  window.URL.revokeObjectURL(fileURL);
}

function useDownloadFile() {
  const { downloadFile } = useDataService(Service.Profitability);

  return async (endpoint: string, filename: string) => {
    const { data, error } = await downloadFile(endpoint);
    if (error) {
      throw new Error(error.message);
    }
    if (!data) {
      return;
    }

    triggerFileDownload(data, filename);
  };
}

export function useDownloadAllCostConfigFiles() {
  const downloadFile = useDownloadFile();

  return () => downloadFile(DOWNLOAD_ALL_CONFIGS_ENDPOINT, 'FeeConfigs.xlsx');
}

export function useDownloadChangesFile(uploadId: string) {
  const downloadFile = useDownloadFile();

  return () => downloadFile(`downloadChanges/${uploadId}`, 'Changes.xlsx');
}

export function useDownloadErrorsFile(uploadId: string) {
  const downloadFile = useDownloadFile();

  return () => downloadFile(`downloadErrors/${uploadId}`, 'Errors.xlsx');
}

export function useDownloadCostConfigFile() {
  const downloadFile = useDownloadFile();

  return (costType: CostConfigKey) => downloadFile(`downloadConfig/${costType}`, `${costType}.xlsx`);
}

export function useUploadCostConfigFile() {
  const { uploadFile } = useDataService(Service.Profitability);

  return async (
    file: File,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal
  ): Promise<CostConfigUploadResult> => {
    const handleProgress = (progressEvent: AxiosProgressEvent) => {
      if (!onProgress) {
        return;
      }
      const total = progressEvent.total ?? progressEvent.loaded;
      const percent = total ? Math.round((progressEvent.loaded * 100) / total) : 0;
      onProgress(percent);
    };

    const { error } = await uploadFile<never>(UPLOAD_CONFIG_ENDPOINT, file, handleProgress, signal);

    if (!error) {
      return { success: true, fileName: file.name, errors: [] };
    }

    const validationErrors = Array.isArray(error.details) ? (error.details as UploadError[]) : undefined;

    return {
      success: false,
      fileName: file.name,
      errors: validationErrors ? validationErrors.map(describeUploadError) : [error.message],
    };
  };
}

export function useCostUploads(isModalOpen: boolean) {
  const { accountNumber } = useContext(TenantConfigContext);

  const requestData = useDataRequest();

  const getData = useDataService(Service.Profitability, DEFAULT_PRO_FILTER_SET).get<CostConfigurationUpload[]>(
    `cost-uploads`
  );

  return useQuery({
    queryKey: ['cost-uploads', accountNumber, requestData, isModalOpen],
    queryFn: async (): Promise<CostConfigurationUpload[]> => {
      const response = await getData('', requestData);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data ?? [];
    },
    staleTime: 0,
  });
}
